import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";

export const HeroCarousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Handle circular navigation
  const prevIndex = (currentIndex - 1 + items.length) % items.length;
  const nextIndex = (currentIndex + 1) % items.length;

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    setIsAutoPlaying(false);
  }, [items.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
    setIsAutoPlaying(false);
  }, [items.length]);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, items.length]);

  return (
    <div className="relative w-full max-w-6xl mx-auto h-[400px] md:h-[500px] flex items-center justify-center overflow-hidden py-8">
      {/* Navigation Buttons - Sticky/Floating */}
      <button
        onClick={handlePrev}
        className="absolute left-4 md:left-8 z-30 p-3 rounded-full bg-slate-800/50 backdrop-blur-md border border-slate-700 hover:bg-white hover:text-slate-900 transition-all duration-300 group"
      >
        <ChevronLeft className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
      </button>

      <button
        onClick={handleNext}
        className="absolute right-4 md:right-8 z-30 p-3 rounded-full bg-slate-800/50 backdrop-blur-md border border-slate-700 hover:bg-white hover:text-slate-900 transition-all duration-300 group"
      >
        <ChevronRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Carousel Track */}
      <div className="relative w-full h-full flex items-center justify-center perspective-1000">
        {items.map((item, index) => {
          let position = "hidden";
          if (index === currentIndex) position = "center";
          else if (index === prevIndex) position = "left";
          else if (index === nextIndex) position = "right";

          // If strictly hidden, don't render to save resources/complication,
          // or render with opacity 0 for transition if desired.
          // Here we render all but control visibility via classes.
          const isVisible = position !== "hidden";

          let translateClass = "";
          let scaleClass = "";
          let zIndexClass = "";
          let opacityClass = "";
          let imageClass = "";

          switch (position) {
            case "center":
              translateClass = "translate-x-0";
              scaleClass = "scale-100";
              zIndexClass = "z-20";
              opacityClass = "opacity-100";
              imageClass = "";
              break;
            case "left":
              translateClass = "-translate-x-[60%] md:-translate-x-[55%]";
              scaleClass = "scale-75";
              zIndexClass = "z-10";
              // Removed blur from container to preserve edge sharpness
              opacityClass = "opacity-40 hover:opacity-60 cursor-pointer";
              // Applied blur and grayscale to the image instead
              imageClass = "grayscale blur-[1px]";
              break;
            case "right":
              translateClass = "translate-x-[60%] md:translate-x-[55%]";
              scaleClass = "scale-75";
              zIndexClass = "z-10";
              // Removed blur from container to preserve edge sharpness
              opacityClass = "opacity-40 hover:opacity-60 cursor-pointer";
              // Applied blur and grayscale to the image instead
              imageClass = "grayscale blur-[1px]";
              break;
            default:
              translateClass = "translate-x-0 opacity-0 scale-50 z-0";
          }

          if (!isVisible) return null;

          return (
            <div
              key={item.id}
              onClick={() => {
                if (position === "left") handlePrev();
                if (position === "right") handleNext();
              }}
              className={`absolute transition-all duration-700 ease-out ${translateClass} ${scaleClass} ${zIndexClass} ${opacityClass} w-[70%] md:w-[60%] aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-slate-700/50`}
            >
              <img
                src={item.image}
                alt={item.title}
                className={`w-full h-full object-cover transition-all duration-500 ${imageClass}`}
              />

              {/* Overlay Content for Center Slide */}
              {position === "center" && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-6 md:p-8">
                  <div className="transform transition-all duration-500 translate-y-0">
                    <span className="inline-block px-3 py-1 mb-3 text-xs font-semibold tracking-wider text-indigo-300 uppercase bg-indigo-500/20 rounded-full backdrop-blur-sm border border-indigo-500/30">
                      Featured
                    </span>
                    <h2 className="text-2xl md:text-4xl font-bold text-white mb-2 tracking-tight">
                      {item.title}
                    </h2>
                    {item.type === "video" && (
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Play className="w-4 h-4 fill-current" />
                        <span>Video Preview</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Video Indicator Icon */}
              {item.type === "video" && position !== "hidden" && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div
                    className={`w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 ${position !== "center" ? "scale-75 opacity-50" : ""}`}
                  >
                    <Play className="w-6 h-6 text-white ml-1 fill-white" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroCarousel;
